import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';

import { BehaviorSubject, combineLatest, Observable, ReplaySubject, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, takeUntil, withLatestFrom } from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { isEqual } from 'lodash-es';

import { ShFormMultiSelectModule } from '../../../../../../form-controls/components/form-multi-select/form-multi-select.module';
import { MultiFormSelectOptions } from '../../../../../../form-controls/entities/multi-form-select-options.entity';
import { MultiFormSelectStyle } from '../../../../../../form-controls/entities/multi-form-select-style.entity';
import { isNotNullOrUndefined } from '../../../../../../helpers/typed-reactive-filters.helper';
import { SortInfo } from '../../entities/sort-info.entity';
import { SortOption } from '../../entities/sort-option.entity';
import { SortOptionToSortInfo } from '../../helpers/sort-option-to-sort-info.helper';
@Component({
    standalone: true,
    selector: 'sh-sort-select',
    templateUrl: './sort-select.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ReactiveFormsModule, ShFormMultiSelectModule]
})
export class ShSortSelectComponent implements OnInit {
    public filtersControl: UntypedFormControl = new UntypedFormControl();
    public sortFormSelectOptions$: Observable<MultiFormSelectOptions>;
    private sortInfoLabels$: Observable<string[]>;

    public loading = false;

    public readonly destroy$: Subject<void> = new Subject<void>();
    public readonly sortInfo$: BehaviorSubject<SortInfo | null> = new BehaviorSubject<SortInfo | null>(null);
    public pureSortInfo$: Observable<SortInfo>;

    public readonly sortOptions$: ReplaySubject<SortOption[]> = new ReplaySubject<SortOption[]>(1);
    public multiSelectStyle: MultiFormSelectStyle = MultiFormSelectStyle.Ghost;

    @Input() prefixMessage: string;
    @Input() loadingMsg: string;

    @Input() set sortInfo(sortInfo: SortInfo | null) {
        this.sortInfo$.next(sortInfo);
    }

    @Input() set sortOptions(sortOptions: SortOption[]) {
        this.sortOptions$.next(sortOptions);
    }

    @Output() sortInfoChanged: EventEmitter<SortInfo> = new EventEmitter<SortInfo>();

    ngOnInit(): void {
        this.initPureSortInfo();
        this.initSortInfoLabels();
        this.initSortFormSelectOptions();
        this.initSortValueSubscriber();
        this.initValueChangesListener();
    }

    private initPureSortInfo(): void {
        this.pureSortInfo$ = this.sortInfo$.pipe(isNotNullOrUndefined());
    }

    private initValueChangesListener(): void {
        const sortInfoFromControl$: Observable<SortInfo | undefined> = this.filtersControl.valueChanges.pipe(
            startWith(this.filtersControl.value),
            filter((values): values is any[] => Array.isArray(values) && values.length > 0),
            filter((values): values is string[] => values.every(value => typeof value === 'string')),
            filter(() => this.filtersControl.dirty),
            map(values => values[0]),
            distinctUntilChanged((x, y) => x === y),
            withLatestFrom(this.sortInfoLabels$, this.sortOptions$),
            map(([value, labels, sortOptions]) => this.getSortInfoByLabel(value, labels, sortOptions))
        );
        const onlyChangedSortInfo$: Observable<SortInfo | undefined> = sortInfoFromControl$.pipe(
            filter(sortInfoFromControl => !isEqual(sortInfoFromControl, this.sortInfo$.value))
        );
        onlyChangedSortInfo$.pipe(takeUntil(this.destroy$)).subscribe(sortInfo => {
            this.sortInfoChanged.emit(sortInfo);
        });
    }

    private initSortValueSubscriber(): void {
        const value$: Observable<string | undefined> = combineLatest([
            this.pureSortInfo$,
            this.sortOptions$,
            this.sortInfoLabels$
        ]).pipe(
            map(([sortInfo, sortOptions, sortInfoLabels]) => {
                return this.getLabelBySortInfo(sortInfo, sortOptions, sortInfoLabels);
            })
        );
        value$.pipe(takeUntil(this.destroy$)).subscribe(value => {
            this.filtersControl.markAsPristine();
            this.filtersControl.setValue([value], { emitEvent: false });
        });
    }

    private initSortInfoLabels(): void {
        this.sortInfoLabels$ = this.sortOptions$.pipe(map(sortOptions => sortOptions.map(option => option.label)));
    }

    private initSortFormSelectOptions(): void {
        this.sortFormSelectOptions$ = this.sortInfoLabels$.pipe(
            map(sortLabels => ({
                options: sortLabels.map(option => ({
                    label: option,
                    value: option
                })),
                isLiveData: false,
                totalResults: sortLabels.length
            }))
        );
    }

    private getSortInfoByLabel(value: string, labels: string[], sortOptions: SortOption[]): SortInfo | undefined {
        const index: number = labels.indexOf(value);
        if (index < 0) {
            return undefined;
        }
        return sortOptions[index] ? SortOptionToSortInfo(sortOptions[index]) : undefined;
    }

    private getLabelBySortInfo(sortInfo: SortInfo, sortOptions: SortInfo[], sortLabels: string[]): string | undefined {
        const index: number = sortOptions.findIndex(
            option => option.field === sortInfo.field && option.direction === sortInfo.direction
        );
        if (index < 0) {
            return undefined;
        }
        return sortLabels[index];
    }
}
